import { Drawer, IconButton, Stack, useTheme } from "@mui/material";
import { ChevronRight } from "../../../../styles/icons/chevron-right";
import { LogoWithText } from "../../../generic/components/logo-with-text";
import { Scrollbar } from "../../../generic/components/scrollbar";
import { useShowSidebar } from "../../hooks/use-show-sidebar";
import { Navigation } from "./navigation/navigation";
import { useSidebarUi } from "./sidebar-ui-context";
import { sidebar2026Width } from "./sidebar-widths";

import type { NavigationProps } from "./navigation/types";
import type { DrawerProps, Theme } from "@mui/material";
import type { FC, PropsWithChildren, ReactElement } from "react";

/**
 * The 2026 sidebar, for the cha_4551_dashboard_2026 flag. A copy of the original Sidebar, kept apart
 * so it can change without touching the original: narrower, in the 2026 colours, with only the logo
 * at its foot. The navigation inside is shared; where the 2026 design differs, it checks
 * useSidebar2026() or reads the CSS variables set here.
 */
export const Sidebar2026: FC<
  PropsWithChildren & { footer?: ReactElement | null } & Pick<DrawerProps, "variant"> &
    NavigationProps
> = ({ children, footer = DEFAULT_FOOTER, variant = "permanent" }) => {
  const { open, toggleSidebar, closeSidebar, layout } = useSidebarUi();

  const theme = useTheme();
  const showSidebar = useShowSidebar();

  const extraProps: DrawerProps = showSidebar
    ? { variant }
    : {
        variant: "temporary",
        ModalProps: { keepMounted: false, disablePortal: true },
        sx: { zIndex: (x: Theme) => x.zIndex.appBar + 100, display: !open ? "none" : "block" },
      };

  return (
    <>
      {!showSidebar && layout === "default" && (
        <IconButton
          data-analytics-id="sidebar-open"
          onClick={toggleSidebar}
          sx={{
            display: {
              xs: "inline-flex",
              lg: "none",
              position: "absolute",
              top: 0,
            },
          }}
          title="Toggle Navigation"
        >
          <ChevronRight fontSize="large" />
        </IconButton>
      )}

      <Drawer
        key={extraProps.variant}
        anchor="left"
        slotProps={{
          paper: {
            sx: {
              "--nav-bg": theme.palette.neutral[900],
              "--nav-services-bg": "#292F3D",
              "--nav-color": theme.palette.common.white,
              "--nav-border-color": "transparent",
              "--nav-logo-border": theme.palette.neutral[700],
              "--nav-item-color": "#DBE0E5",
              // Items start flush with the navigation, with no padding of their own.
              "--nav-item-pl": "0px",
              "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
              "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
              "--nav-item-active-color": theme.palette.common.white,
              "--nav-item-disabled-color": theme.palette.neutral[500],
              "--nav-item-icon-color": "#DBE0E5",
              "--nav-item-icon-active-color": theme.palette.primary.main,
              "--nav-item-icon-disabled-color": theme.palette.neutral[500],
              "--nav-item-chevron-color": theme.palette.neutral[600],
              "--nav-scrollbar-color": theme.palette.neutral[400],
              backgroundColor: "var(--nav-bg)",
              borderRightColor: "var(--nav-border-color)",
              borderRightStyle: "solid",
              borderRightWidth: 1,
              color: "var(--nav-color)",
              width: sidebar2026Width,
              visibility: !showSidebar && !open ? "hidden" : "visible",
            },
          },
        }}
        open={open}
        onClose={closeSidebar}
        {...extraProps}
      >
        <Scrollbar
          sx={{
            height: `calc(100vh - ${FOOTER_HEIGHT}px)`,
            minHeight: "5%",
            "& .simplebar-content": {
              height: "87%",
            },
            "& .simplebar-scrollbar:before": {
              background: "var(--nav-scrollbar-color)",
            },
          }}
        >
          {children || (
            <Navigation {...(extraProps.variant === "temporary" && { onClose: closeSidebar })} />
          )}
        </Scrollbar>
        {footer}
      </Drawer>
    </>
  );
};

// Room for the logo (200px wide, so about 29px tall) and its padding; the navigation scrolls above it.
const FOOTER_HEIGHT = 56;

const Branding: FC = () => (
  <Stack
    sx={{
      width: "100%",
      // The logo sits at the foot of the sidebar.
      justifyContent: "flex-end",
      px: 1,
      pt: 1,
      pb: 2,
      height: `${FOOTER_HEIGHT}px`,
      position: "absolute",
      bottom: 0,
      left: 0,
      zIndex: 1,
      backgroundColor: "var(--nav-bg)",
    }}
  >
    <LogoWithText sx={{ width: 200 }} />
  </Stack>
);

const DEFAULT_FOOTER = <Branding />;
